import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Linkify } from "@/lib/linkify";
import ImageZoom from "@/components/ImageZoom";

const fmt = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");
const STATUS_BADGE = { yes: "bg-olive/25 text-dark-olive", no: "bg-maroon/15 text-maroon", maybe: "bg-gold/25 text-ink" };
const QUICK_LABEL = { yes: "Yes", no: "No", maybe: "Maybe" };

// Recipient-facing inbox card. Messages live only here (never emailed). Shows the
// enabled quick responses (Yes/No/Maybe) plus a threaded, back-and-forth Reply
// conversation when the sender enabled replies.
export default function MyMessages({ asPage = false }) {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [openReply, setOpenReply] = useState({});

  const load = useCallback(async () => {
    try { const r = await api.get("/messages/mine"); setItems(r.data.items || []); } catch (e) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const quick = async (mid, response) => {
    try { await api.post(`/messages/${mid}/respond`, { response }); toast.success(`Replied ${QUICK_LABEL[response]}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not respond"); }
  };
  const sendReply = async (mid) => {
    const text = (drafts[mid] || "").trim();
    if (!text) return toast.error("Write a reply first.");
    try {
      await api.post(`/messages/${mid}/respond`, { reply_text: text });
      toast.success("Reply sent"); setDrafts({ ...drafts, [mid]: "" }); setOpenReply({ ...openReply, [mid]: false }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send reply"); }
  };

  if (items.length === 0) {
    if (!asPage) return null;
    return (
      <div data-testid="messages-page">
        <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-4">Messages</h1>
        <div className="bg-cream border border-ink/15 rounded-xl p-8 text-center text-ink/55" data-testid="messages-empty">No messages yet.</div>
      </div>
    );
  }
  return (
    <div className={asPage ? "" : ""} data-testid={asPage ? "messages-page" : undefined}>
      {asPage && <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-4">Messages</h1>}
      <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="my-messages">
      <div className="flex items-center gap-2 mb-3"><Icon name="MessageSquare" className="w-5 h-5 text-maroon" /><h2 className="font-zilla text-xl font-semibold">Messages for you</h2></div>
      <div className="space-y-2">
        {items.map((m) => {
          const quicks = m.quick_methods || [];
          const thread = m.thread || [];
          return (
            <div key={m.message_id} data-testid={`mymsg-${m.message_id}`} className="bg-warm-cream border border-ink/10 rounded-lg px-3 py-2.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold">{m.subject}</div>
                  <div className="text-[13px] text-ink/65 whitespace-pre-wrap"><Linkify text={m.note} /></div>
                  {m.image_url && <ImageZoom src={m.image_url} testid={`mymsg-image-${m.message_id}`} className="mt-1.5 max-h-48 rounded-lg border border-ink/15" />}
                  <div className="text-[12px] text-ink/45 mt-0.5">from {m.sender_name} · {fmt(m.created_at)}</div>
                </div>
                {m.status !== "awaiting" && <span data-testid={`mymsg-status-${m.message_id}`} className={`text-[12px] px-2 py-0.5 rounded-full font-semibold shrink-0 capitalize ${STATUS_BADGE[m.status] || ""}`}>{m.status}</span>}
              </div>

              {thread.length > 0 && (
                <div className="mt-2 space-y-1.5" data-testid={`mymsg-thread-${m.message_id}`}>
                  {thread.map((t, i) => (
                    <div key={i} className={`text-[13px] rounded-lg px-2.5 py-1.5 ${t.from === "recipient" ? "bg-cream border-l-2 border-dark-olive ml-6" : "bg-maroon/8 border-l-2 border-maroon mr-6"}`}>
                      <div className="text-[11px] font-semibold text-ink/50">{t.from === "recipient" ? "You" : (t.by_name || m.sender_name)}</div>
                      <div className="whitespace-pre-wrap"><Linkify text={t.text} /></div>
                    </div>
                  ))}
                </div>
              )}

              {!m.no_reply_needed && quicks.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2 items-center">
                  {quicks.includes("yes") && <button data-testid={`mymsg-yes-${m.message_id}`} onClick={() => quick(m.message_id, "yes")} className={`rounded-lg px-3 py-1.5 min-h-[38px] text-[13px] font-semibold ${m.status === "yes" ? "bg-dark-olive text-cream" : "border border-dark-olive/40 text-dark-olive"}`}>Yes</button>}
                  {quicks.includes("no") && <button data-testid={`mymsg-no-${m.message_id}`} onClick={() => quick(m.message_id, "no")} className={`rounded-lg px-3 py-1.5 min-h-[38px] text-[13px] font-semibold ${m.status === "no" ? "bg-maroon text-cream" : "border border-maroon/40 text-maroon"}`}>No</button>}
                  {quicks.includes("maybe") && <button data-testid={`mymsg-maybe-${m.message_id}`} onClick={() => quick(m.message_id, "maybe")} className={`rounded-lg px-3 py-1.5 min-h-[38px] text-[13px] font-semibold ${m.status === "maybe" ? "bg-gold text-ink" : "border border-gold/60 text-ink"}`}>Maybe</button>}
                </div>
              )}

              {m.reply_enabled && (
                <div className="mt-2">
                  {openReply[m.message_id] ? (
                    <div className="flex gap-2">
                      <input data-testid={`mymsg-reply-text-${m.message_id}`} value={drafts[m.message_id] || ""} onChange={(e) => setDrafts({ ...drafts, [m.message_id]: e.target.value })} placeholder="Write a reply…" className="flex-1 bg-cream border border-ink/15 rounded-lg px-3 py-2 text-[14px]" onKeyDown={(e) => e.key === "Enter" && sendReply(m.message_id)} />
                      <button data-testid={`mymsg-reply-send-${m.message_id}`} onClick={() => sendReply(m.message_id)} className="bg-maroon text-cream rounded-lg px-3 min-h-[38px] text-[13px] font-semibold">Send</button>
                    </div>
                  ) : (
                    <button data-testid={`mymsg-reply-${m.message_id}`} onClick={() => setOpenReply({ ...openReply, [m.message_id]: true })} className="text-[13px] text-maroon font-semibold flex items-center gap-1"><Icon name="MessageSquare" className="w-3.5 h-3.5" /> {thread.length ? "Reply again" : "Reply"}</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}
